// src/components/forms/RichTextArea.tsx
import React, { useRef } from 'react';
import { TextArea } from './TextArea';
import { 
  Bold, Italic, Heading1, Heading2, 
  List, ListOrdered, Quote, Link, Code 
} from 'lucide-react';

interface RichTextAreaProps {
  label?: string;
  name: string;
  value: string;
  onChange: (e: any) => void;
  placeholder?: string;
  rows?: number;
  className?: string;
}

export const RichTextArea: React.FC<RichTextAreaProps> = ({
  label,
  name,
  value,
  onChange,
  placeholder,
  rows = 6,
  className
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // 🛠️ DICTIONARY: All possible formatting transformations
  const tools = [
    { label: 'Bold', icon: <Bold size={15} />, apply: (t: string) => `**${t || 'bold text'}**` },
    { label: 'Italic', icon: <Italic size={15} />, apply: (t: string) => `*${t || 'italic text'}*` },
    { label: 'Heading 1', icon: <Heading1 size={15} />, apply: (t: string) => `\n# ${t || 'Heading 1'}\n` },
    { label: 'Heading 2', icon: <Heading2 size={15} />, apply: (t: string) => `\n## ${t || 'Heading 2'}\n` },
    { label: 'Bullet List', icon: <List size={15} />, apply: (t: string) => `\n• ${t || 'List item'}` },
    { label: 'Numbered List', icon: <ListOrdered size={15} />, apply: (t: string) => `\n1. ${t || 'List item'}` },
    { label: 'Quote', icon: <Quote size={15} />, apply: (t: string) => `\n> ${t || 'Blockquote'}\n` },
    { label: 'Code Block', icon: <Code size={15} />, apply: (t: string) => `\n\`\`\`\n${t || 'code here'}\n\`\`\`\n` },
    { label: 'Insert Link', icon: <Link size={15} />, apply: (t: string) => `[${t || 'Link Text'}](https://example.com)` },
  ];

  const handleFormat = (applyStyle: (text: string) => string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentText = value || '';

    const selectedText = currentText.substring(start, end);
    const transformedText = applyStyle(selectedText);
    
    const newText = currentText.substring(0, start) + transformedText + currentText.substring(end);

    // Call your existing form state updater mock event object
    onChange({
      target: { name, value: newText }
    });

    // Refocus cursor seamlessly right after transformation ends
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + transformedText.length, start + transformedText.length);
    }, 0);
  };

  return (
    <div className={`w-full ${className}`}>
      {label && (
        <label className="text-sm font-bold uppercase text-zinc-400 dark:text-indigo-200 ml-1 block mb-1.5">
          {label}
        </label>
      )}

      {/* Toolbar Layer */}
      <div className="flex flex-wrap items-center gap-1 bg-zinc-50 dark:bg-zinc-800/60 border border-zinc-200 dark:border-zinc-700/80 rounded-t-xl p-2 relative z-10">
        {tools.map((tool, idx) => (
          <button
            key={idx}
            type="button"
            onClick={() => handleFormat(tool.apply)}
            className="p-2 text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-zinc-200 dark:hover:bg-zinc-700 rounded-lg transition-all"
            title={tool.label}
          >
            {tool.icon}
          </button>
        ))}
      </div>

      {/* Input Layer */}
      <TextArea
        ref={textareaRef}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        rows={rows}
        textareaClassName="rounded-t-none border-t-0 focus:ring-0 group-focus-within:border-transparent rounded-b-xl"
      />
    </div>
  );
};